/* Video Banner with Intro — 707dot
   One-time logo reveal, logo-to-header handoff and header UI entrance. */

(() => {
  if (window.__sevenZeroSevenIntroInitialized) return;
  window.__sevenZeroSevenIntroInitialized = true;

  const STORAGE_KEY = 'shopifyIntroPlayed';
  const root = document.documentElement;
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const designMode = Boolean(window.Shopify && Shopify.designMode);
  const sleep = (duration) => new Promise((resolve) => window.setTimeout(resolve, duration));
  const waitForPaint = () =>
    new Promise((resolve) => {
      requestAnimationFrame(() => requestAnimationFrame(resolve));
    });

  const releaseBootGuard = async (overlay) => {
    if (!root.classList.contains('intro-boot-pending')) return;

    // Flush the real fixed overlay, keep the guard for two painted frames,
    // then swap identical black layers without exposing the page below.
    void overlay.offsetWidth;
    await waitForPaint();
    root.classList.remove('intro-boot-pending');
  };

  const getStoredState = () => {
    try {
      return sessionStorage.getItem(STORAGE_KEY);
    } catch (error) {
      return null;
    }
  };

  const storePlayedState = () => {
    try {
      sessionStorage.setItem(STORAGE_KEY, 'true');
    } catch (error) {
      // The animation should still complete when storage is unavailable.
    }
  };

  const decodeImage = async (image) => {
    if (!image || typeof image.decode !== 'function') return;

    try {
      await Promise.race([image.decode(), sleep(900)]);
    } catch (error) {
      // A failed decode must never trap the visitor behind the intro.
    }
  };

  const startVideo = (video) => {
    if (!video) return;

    video.muted = true;
    video.defaultMuted = true;
    video.setAttribute('playsinline', '');
    const playPromise = video.play();
    if (playPromise && typeof playPromise.catch === 'function') {
      playPromise.catch(() => {});
    }
  };

  const dispatchIntroComplete = () => {
    document.dispatchEvent(new CustomEvent('sevenzeroseven:intro-complete'));
  };

  const revealHeader = (
    animate,
    { hideLogo = false, dispatchEvent = true, keepTravelLock = false } = {}
  ) => {
    const sectionHeader = document.querySelector('.section-header');

    if (sectionHeader) {
      sectionHeader.classList.toggle('header-logo-handoff', hideLogo);
    }

    root.classList.remove('intro-boot-pending', 'intro-sequence-pending');
    if (!keepTravelLock) root.classList.remove('intro-logo-travelling', 'intro-using-header-logo');
    root.classList.add('intro-sequence-complete');

    if (sectionHeader) {
      sectionHeader.classList.remove('header-entering', 'header-ready');

      if (animate && !reduceMotion) {
        // Force a fresh animation even after a Shopify section refresh.
        void sectionHeader.offsetWidth;
        sectionHeader.classList.add('header-entering');
      } else {
        sectionHeader.classList.add('header-ready');
      }
    }

    if (dispatchEvent) dispatchIntroComplete();
    return sectionHeader;
  };

  const fadeAndRemoveOverlay = async (overlay) => {
    overlay.classList.add('is-hidden');
    await sleep(reduceMotion ? 0 : 400);
    overlay.remove();
  };

  const getHeaderLogoTarget = () => {
    const sectionHeader = document.querySelector('.section-header');
    if (!sectionHeader) return null;

    // Query each target separately. A comma-separated query follows DOM order,
    // so the padded link can be returned before its image even when the image
    // selector is written first. That 15px size difference caused the final
    // handoff frame to visibly shrink from the link box to the real logo.
    return (
      sectionHeader.querySelector('.header__heading-logo') ||
      sectionHeader.querySelector('.header__heading-logo-wrapper') ||
      sectionHeader.querySelector('.header__heading-link')
    );
  };

  const runHeaderLogoIntro = async (section, overlay, placeholder, logo, hold, backdropDuration, moveDuration) => {
    // The actual Header image never changes parent, bitmap or blend context.
    // Its animation ends at its natural layout box, so there is no handoff frame.
    logo.removeAttribute('srcset');
    logo.removeAttribute('sizes');
    await decodeImage(logo);
    const target = logo.getBoundingClientRect();
    const center = placeholder.getBoundingClientRect();
    if (!target.width || !target.height || !center.width) return false;

    const scale = placeholder.offsetWidth / target.width;
    const x = center.left + center.width / 2 - (target.left + target.width / 2);
    const y = center.top + center.height / 2 - (target.top + target.height / 2);
    const centralTransform = `translate3d(${x}px, ${y}px, 0) scale(${scale})`;
    let entrance;
    let travel;
    try {
      entrance = logo.animate([
        { transform: `translate3d(${x}px, ${y}px, 0) scale(${scale * 0.86})`, opacity: 0, filter: 'blur(8px)' },
        { transform: centralTransform, opacity: 1, filter: 'blur(0)' }
      ], { duration: 1400, easing: 'cubic-bezier(0.16, 1, 0.3, 1)', fill: 'forwards' });
      root.classList.add('intro-using-header-logo', 'intro-logo-travelling');
      overlay.classList.add('is-logo-visible');
      await entrance.finished;
      // Preserve the previous overall fade-in + hold timing.
      await sleep(Math.max(0, hold - 300));

      const duration = Math.max(300, backdropDuration);
      await window.__sevenHomeVideoReady;
      overlay.style.setProperty('--intro-backdrop-duration', `${duration}ms`);
      overlay.classList.add('is-revealing');
      travel = logo.animate([
        { transform: centralTransform, filter: 'blur(0)' },
        { transform: 'translate3d(0, 0, 0) scale(1)', filter: 'blur(0)' }
      ], { duration: Math.max(600, moveDuration), easing: 'cubic-bezier(0.76, 0, 0.24, 1)', fill: 'forwards' });
      entrance.cancel();
      entrance = null;
      await Promise.all([travel.finished, sleep(duration)]);

      revealHeader(true, { dispatchEvent: false, keepTravelLock: true });
      section.classList.add('is-intro-complete');
      // Identity transform matches the resting image: cancellation is geometric
      // continuity on the same node, with no opacity or visibility exchange.
      travel.cancel();
      travel = null;
      root.classList.remove('intro-using-header-logo', 'intro-logo-travelling');
      dispatchIntroComplete();
      storePlayedState();
      await fadeAndRemoveOverlay(overlay);
      return true;
    } finally {
      entrance?.cancel();
      travel?.cancel();
      root.classList.remove('intro-using-header-logo', 'intro-logo-travelling');
    }
  };

  const runIntro = async (section, overlay) => {
    const logo = overlay.querySelector('[data-intro-logo]');
    const video = section.querySelector('video');
    const delay = Number.parseInt(section.dataset.introDelay || '300', 10);
    const hold = Number.parseInt(section.dataset.introHold || '2000', 10);
    const backdropDuration = Number.parseInt(section.dataset.introLogoFade || '1200', 10);
    const moveDuration = Number.parseInt(section.dataset.introMove || '1300', 10);

    startVideo(video);

    if (reduceMotion || designMode || getStoredState() || root.classList.contains('intro-sequence-complete')) {
      section.classList.add('is-intro-complete');
      overlay.remove();
      revealHeader(false);
      return;
    }

    // Keep the overlay displayed through header reveal and the final handoff.
    // intro-sequence-pending ends before the outgoing logo has finished fading.
    overlay.classList.add('is-active');
    await releaseBootGuard(overlay);

    await decodeImage(logo);
    await sleep(Math.max(0, delay));

    const existingLogo = getHeaderLogoTarget();
    if (section.dataset.introMatchHeader === 'true' && logo && existingLogo?.matches('img') && typeof existingLogo.animate === 'function') {
      if (await runHeaderLogoIntro(section, overlay, logo, existingLogo, Math.max(0, hold), backdropDuration, moveDuration)) return;
    }

    overlay.classList.add('is-logo-visible');
    if (logo) logo.classList.add('is-visible');

    // Let the original central fade-in finish, then retain the requested hold.
    await sleep(1100 + Math.max(0, hold));

    const headerTarget = getHeaderLogoTarget();
    await decodeImage(headerTarget && headerTarget.matches('img') ? headerTarget : headerTarget?.querySelector('img'));

    const effectiveBackdropDuration = Math.max(300, backdropDuration);
    await window.__sevenHomeVideoReady;
    overlay.style.setProperty('--intro-backdrop-duration', `${effectiveBackdropDuration}ms`);
    overlay.classList.add('is-revealing');
    root.classList.add('intro-logo-travelling');
    const backdropReady = sleep(effectiveBackdropDuration);

    if (logo && headerTarget && typeof logo.animate === 'function') {
      const from = logo.getBoundingClientRect();
      const to = headerTarget.getBoundingClientRect();

      if (from.width > 0 && from.height > 0 && to.width > 0 && to.height > 0) {
        const deltaX = to.left + to.width / 2 - (from.left + from.width / 2);
        const deltaY = to.top + to.height / 2 - (from.top + from.height / 2);
        const targetScaleX = Math.max(0.08, Math.min(1.25, to.width / from.width));
        const targetScaleY = Math.max(0.08, Math.min(1.25, to.height / from.height));

        logo.classList.add('is-travelling');
        const travel = logo.animate(
          [
            { transform: 'translate3d(0, 0, 0) scale(1, 1)', filter: 'blur(0)' },
            {
              transform: `translate3d(${deltaX}px, ${deltaY}px, 0) scale(${targetScaleX}, ${targetScaleY})`,
              filter: 'blur(0)'
            }
          ],
          {
            duration: Math.max(600, moveDuration),
            easing: 'cubic-bezier(0.76, 0, 0.24, 1)',
            fill: 'forwards'
          }
        );

        await Promise.all([travel.finished.catch(() => {}), backdropReady]);
        const sectionHeader = revealHeader(true, {
          hideLogo: true,
          dispatchEvent: false,
          keepTravelLock: true
        });
        section.classList.add('is-intro-complete');

        // Keep both decoded logos perfectly aligned while their opacity trades
        // places. Avoiding DOM reparenting prevents a one-frame compositor flash
        // when the image enters the Header's mix-blend layer.
        await waitForPaint();
        const handoffFade = logo.animate(
          [{ opacity: 1 }, { opacity: 0 }],
          {
            duration: 160,
            easing: 'linear',
            fill: 'forwards'
          }
        );
        sectionHeader?.classList.remove('header-logo-handoff');
        await handoffFade.finished.catch(() => {});
        travel.cancel();
        logo.style.visibility = 'hidden';
        logo.style.opacity = '0';
        root.classList.remove('intro-logo-travelling');
        dispatchIntroComplete();
      } else {
        if (logo) logo.style.opacity = '0';
        revealHeader(true);
        section.classList.add('is-intro-complete');
      }
    } else {
      if (logo) logo.style.opacity = '0';
      revealHeader(true);
      section.classList.add('is-intro-complete');
    }

    storePlayedState();
    await fadeAndRemoveOverlay(overlay);
  };

  const section = document.querySelector('.video-banner-with-intro');
  const overlay = section?.querySelector('[data-intro-overlay]');

  if (!section || !overlay) {
    document.querySelectorAll('.video-banner-with-intro video').forEach(startVideo);
    revealHeader(false);
    return;
  }

  runIntro(section, overlay).catch(async () => {
    startVideo(section.querySelector('video'));
    section.classList.add('is-intro-complete');
    revealHeader(false);
    storePlayedState();
    await fadeAndRemoveOverlay(overlay);
  });
})();
